import { epochDay } from "../lib/date";
import type { Grade, Profile, SrsCard } from "../state/profile";
import { isMastered } from "./mastery";

export const LEARNING_STEPS_DAYS = [0, 1, 3] as const;
export const GRADUATION_DAYS = LEARNING_STEPS_DAYS[LEARNING_STEPS_DAYS.length - 1]!;
export const EASE_MIN = 1.3;
export const EASE_MAX = 3.0;
export const EASE_START = 2.3;
export const INTERVAL_CAP_DAYS = 180;
export const FUZZ = 0.1; // ±10%

export function newCard(id: string, today: string): SrsCard {
  return { id, due: epochDay(today), interval: 0, ease: EASE_START, reps: 0, lapses: 0, lastGrade: 2 };
}

function clampEase(e: number): number {
  return Math.min(EASE_MAX, Math.max(EASE_MIN, e));
}

function nextLearningStep(current: number): number {
  const idx = LEARNING_STEPS_DAYS.indexOf(current as (typeof LEARNING_STEPS_DAYS)[number]);
  return LEARNING_STEPS_DAYS[Math.min(idx + 1, LEARNING_STEPS_DAYS.length - 1)]!;
}

function applyFuzz(days: number, rng: () => number): number {
  if (days <= 2) return days; // don't fuzz the same-session/1-day learning steps
  const factor = 1 + (rng() * 2 - 1) * FUZZ;
  return Math.max(1, Math.round(days * factor));
}

/** SM-2-derived grading. A card "in learning" (interval < GRADUATION_DAYS)
    walks the fixed LEARNING_STEPS_DAYS ladder; once graduated it grows by
    ease like standard SM-2. A heavy lapse can push interval back under the
    graduation threshold, which correctly re-enters the learning ladder —
    that's intentional, not a bug. */
export function gradeCard(card: SrsCard, grade: Grade, today: string, rng: () => number = Math.random): SrsCard {
  const wasLearning = card.interval < GRADUATION_DAYS;
  let interval: number;
  let ease = card.ease;
  let lapses = card.lapses;

  if (wasLearning) {
    if (grade === 0) interval = LEARNING_STEPS_DAYS[0];
    else if (grade === 1) interval = card.interval;
    else if (grade === 2) interval = nextLearningStep(card.interval);
    else interval = LEARNING_STEPS_DAYS[LEARNING_STEPS_DAYS.length - 1]!; // Easy graduates immediately
  } else if (grade === 0) {
    interval = Math.max(1, Math.round(card.interval * 0.3));
    ease = clampEase(ease - 0.2);
    lapses += 1;
  } else if (grade === 1) {
    interval = Math.max(1, Math.round(card.interval * 0.6));
    ease = clampEase(ease - 0.15);
  } else if (grade === 2) {
    interval = Math.round(card.interval * card.ease);
    ease = clampEase(ease + 0.05);
  } else {
    interval = Math.round(card.interval * card.ease * 1.35);
    ease = clampEase(ease + 0.15);
  }

  interval = Math.min(INTERVAL_CAP_DAYS, interval);
  const fuzzedInterval = applyFuzz(interval, rng);
  const due = epochDay(today) + fuzzedInterval;

  const next: SrsCard = { ...card, interval, ease, lapses, reps: card.reps + 1, lastGrade: grade, due };
  if (!isMastered(card) && isMastered(next)) next.masteredAt = today;
  return next;
}

export function dueCards(profile: Profile, today: string): string[] {
  const todayEpoch = epochDay(today);
  return Object.values(profile.cards)
    .filter((c) => c.due <= todayEpoch)
    .sort((a, b) => a.due - b.due)
    .map((c) => c.id);
}

/** 1 = just reviewed (fresh/hot), 0 = at or past its due date (cold — the
    "needs a tune-up" state that pulls a card back into the Queue). */
export function heatFor(card: SrsCard | undefined, today: string): number {
  if (!card) return 0;
  const daysUntilDue = card.due - epochDay(today);
  if (card.interval <= 0) return daysUntilDue > 0 ? 1 : 0;
  return Math.max(0, Math.min(1, daysUntilDue / card.interval));
}
